import React from 'react';
import { Chip } from '@jotpsych/ui';
import { alpha, Box, Button, Stack, Typography } from '@mui/material';

import type { SharedCatalogEntry } from '../types';
import { flagTitle } from '../utils/versionSignals';

export interface SharedCatalogPreviewProps {
  entries: SharedCatalogEntry[];
  /** Preview-stage archives: rows the admin drops while scanning the list. */
  archivedIds: ReadonlySet<string>;
  onToggleArchive: (id: string) => void;
  disabled?: boolean;
}

/**
 * The Confirm step's payoff: the actual shared catalog the practice will
 * live with, rendered in the scheduling picker's own order (usage desc).
 * This is the one representation where residual mess is visible to a human
 * eye — so each row keeps the junk-name flag and a lightweight archive
 * toggle for the "one more dead type" an admin spots while scanning.
 * Deliberately NOT an editing surface beyond that: joining lives in the
 * lanes (re-run the wizard) and, later, the standing catalog manager.
 * Pure presentation.
 */
export const SharedCatalogPreview: React.FC<SharedCatalogPreviewProps> = ({
  entries,
  archivedIds,
  onToggleArchive,
  disabled = false,
}) => {
  if (entries.length === 0) return null;
  const keptCount = entries.filter((e) => !archivedIds.has(e.version.id)).length;
  // Same-template siblings among the kept rows — the "do I really want
  // both?" pointer back to the loose-ends near-match tier.
  const templatePeers = new Map<string, string[]>();
  for (const e of entries) {
    if (archivedIds.has(e.version.id) || !e.version.templateId) continue;
    templatePeers.set(e.version.templateId, [
      ...(templatePeers.get(e.version.templateId) ?? []),
      e.name,
    ]);
  }

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        borderRadius: 1.5,
        mb: 2,
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(theme) => ({
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
          <Typography variant="subtitle2">
            📅 Your shared catalog — what providers will pick from
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {keptCount} types, most used first
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Give it a scan — spot one more dead type? Archive it right here.
        </Typography>
      </Box>
      <Box sx={{ px: 0.5, py: 0.25 }}>
        {entries.map((entry, index) => {
          const archived = archivedIds.has(entry.version.id);
          const nameFlag = flagTitle(entry.name);
          return (
            <Box
              key={entry.version.id}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.25,
                borderBottom:
                  index < entries.length - 1
                    ? `1px solid ${theme.palette.divider}`
                    : 'none',
                opacity: archived ? 0.45 : 1,
                '&:hover': { bgcolor: theme.palette.action.hover },
              })}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: 600,
                  minWidth: 0,
                  textDecoration: archived ? 'line-through' : 'none',
                }}
              >
                {entry.name}
              </Typography>
              {entry.isNew && !archived && (
                <Chip size="xSmall" variant="soft" color="success" label="new" />
              )}
              {entry.renamedFrom && !archived && (
                <Chip
                  size="xSmall"
                  variant="soft"
                  color="info"
                  label={`was "${entry.renamedFrom}"`}
                />
              )}
              {nameFlag && !archived && (
                <Chip
                  size="xSmall"
                  variant="soft"
                  color="error"
                  label={`name ${nameFlag}`}
                />
              )}
              {!archived &&
                entry.version.templateId &&
                (templatePeers.get(entry.version.templateId)?.length ?? 0) >
                  1 && (
                  <Chip
                    size="xSmall"
                    variant="soft"
                    color="info"
                    label={`shares template with ${
                      templatePeers
                        .get(entry.version.templateId)
                        ?.filter((n) => n !== entry.name)
                        .slice(0, 1)[0] ?? 'another type'
                    }${
                      (templatePeers.get(entry.version.templateId)?.length ?? 0) > 2
                        ? ' +…'
                        : ''
                    }`}
                  />
                )}
              <Typography variant="caption" color="text.secondary" noWrap>
                {(entry.mergedDefaults ?? entry.version.defaults)
                  .durationLabel &&
                  `${(entry.mergedDefaults ?? entry.version.defaults).durationLabel} · `}
                {entry.version.templateTitle ?? 'no template'} ·{' '}
                {entry.combinedUsage ?? entry.version.usage} visits
                {entry.combinedUsage !== undefined &&
                  entry.combinedUsage !== entry.version.usage &&
                  ' (combined)'}
              </Typography>
              <Button
                size="small"
                variant="text"
                color={archived ? 'primary' : 'inherit'}
                disabled={disabled}
                onClick={() => onToggleArchive(entry.version.id)}
                sx={{ ml: 'auto', flexShrink: 0 }}
              >
                {archived ? 'Restore' : 'Archive'}
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
